'use client';

import React, { useState } from 'react';
import { Error, FormBuilder, LoadingOverlay, useConfig } from '@ibe/components';
import { Col, Row } from 'reactstrap';
import sanitize from '@/Util/sanitize';
import Keys from '@/Translations/generated/en/newsletter.json.keys';
import newsletterFormConfig, { termsAndConditionsKey } from '@/Config/newsletterFormConfig';
import { useTranslation } from '@/app/i18n/client';
import { ApiActivity, ApiActivityType } from '@ibe/api';
import { useRouter } from 'next/navigation';
import { getLinkUrl } from '../linkList/Link';
import { useApi } from '@/Util/useApi';

interface Props {
  text?: string;
  termsAndConditions?: string;
  successfulUrl: string;
  failureUrl: string;
}

const Newsletter = ({ successfulUrl, failureUrl, text, termsAndConditions }: Props) => {
  const { t } = useTranslation('newsletter');
  const api = useApi(true);
  const router = useRouter();
  const config = useConfig();

  const [showTermsAndConditionsError, setShowTermsAndConditionsError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const submitNewsletter = (values: Record<string, string>) => {
    if (termsAndConditions && !values[termsAndConditionsKey]) {
      return setShowTermsAndConditionsError(true);
    }
    setIsLoading(true);
    const activityText = `A user requests a newsletter subscription: ${values.firstName}, ${values.lastName}, ${values.email}`;

    const activity: ApiActivity = {
      text: activityText,
      type: ApiActivityType.NEWSLETTERSUBSCRIPTION
    };

    api
      .createActivity(activity)
      .then(() => router.push(getLinkUrl(successfulUrl, config)))
      .catch(() => router.push(getLinkUrl(failureUrl, config)))
      .finally(() => setIsLoading(false));
  };

  return (
    <LoadingOverlay isLoading={isLoading}>
      <div className={'newsletter-wrapper'}>
        <div className={'newsletter'}>
          {text && <div dangerouslySetInnerHTML={{ __html: sanitize(text) }} />}
          <Row className={'d-flex align-items-center newsletter__form-container'}>
            <Col className="newsletter__form">
              <FormBuilder
                submitButtonText={t(Keys.signUp)}
                formConfig={newsletterFormConfig(t, termsAndConditions)}
                onSubmit={submitNewsletter}
              />
            </Col>
          </Row>
          {showTermsAndConditionsError && (
            <Row>
              <Col>
                <div className="my-1">
                  <Error message={t(Keys.termsAndConditionsError)} />
                </div>
              </Col>
            </Row>
          )}
        </div>
      </div>
    </LoadingOverlay>
  );
};

export default Newsletter;
